<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';

import { type GetCompanyInfoByTinResponseDto, type GetCompanyInfoByTinResponseDtoCompany, apiGetCompanyInfo } from '~/apiClient';

type Model = Partial<GetCompanyInfoByTinResponseDtoCompany>;

// PROPS
const { countryCode, disabled } = defineProps({
    countryCode: { type: String, default: undefined },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: undefined },
});

// EVENTS
const emit = defineEmits(['inputClick']);

// MODEL
const model = defineModel<Model | undefined>();

// DATA
const inputModel = ref<string | undefined>();
const inputModelDebounced = refDebounced(inputModel, 200);
const isChecked = ref<boolean>(false);

const { data, isLoading } = useQuery({
    queryKey: [countryCode, inputModelDebounced],
    enabled: computed(() => !!countryCode && !!inputModelDebounced.value),
    queryFn: async () => apiGetCompanyInfo(countryCode, inputModelDebounced),
});
const companyInfo = computed<GetCompanyInfoByTinResponseDto | undefined>(() => (data.value?.company.tin ? data.value : undefined));

const inputDisabled = computed(() => disabled || !countryCode || isChecked.value);

// METHODS
const onChange = (): void => {
    model.value = inputModel.value ? { tin: inputModel.value } : undefined;
};

const onCheck = (value: boolean | 'indeterminate'): void => {
    if (value === true) {
        model.value = companyInfo.value?.company;
    }

    if (value === false) {
        model.value = { tin: inputModel.value };
    }
};

const formatBusinessNameAndTin = (company: GetCompanyInfoByTinResponseDtoCompany): string | undefined => {
    if (company.businessName) {
        if (company.tin) return `${company.businessName} (${company.tin})`;

        return company.businessName;
    }
};

// WATCHERS
watch(
    () => countryCode,
    () => {
        inputModel.value = undefined;
        model.value = undefined;
        isChecked.value = false;
    }
);
</script>

<template>
    <UInput
        v-model="inputModel"
        class="w-full"
        size="xl"
        :placeholder="placeholder"
        :loading="isLoading"
        :disabled="inputDisabled"
        autocomplete="off"
        @change="onChange"
        @click="emit('inputClick')"
    />

    <Transition>
        <div v-if="companyInfo" class="mt-2">
            <UCheckbox
                color="primary"
                variant="card"
                :ui="{ root: 'items-center' }"
                indicator="end"
                :v-model="isChecked"
                @update:model-value="onCheck"
            >
                <template #label>
                    <div class="flex items-center gap-3">
                        <UIcon name="lucide:building-2" class="size-5 text-neutral-500" />
                        <div class="ml-1 flex flex-col gap-2 text-xs leading-3">
                            <p class="uppercase">{{ formatBusinessNameAndTin(companyInfo.company) }}</p>
                            <p class="text-neutral-500 uppercase">{{ companyInfo.company.city }}</p>
                        </div>
                    </div>
                </template>
            </UCheckbox>
        </div>
    </Transition>
</template>
